/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import Image from 'next/image';
import React, { useEffect, useState, useRef } from 'react';
import { createClient } from '@/app/[locale]/utils/supabase/client';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { buildHref } from '@/i18n/routing';
import { useTheme } from '@/app/[locale]/ThemeProvider';
import SpaceIconWhite from '@/public/SpaceIcon3White.svg';
import SpaceIconAbyss from '@/public/SpaceIcon3Abyss.svg';

export interface Article {
  id: string;
  title: string;
  image?: string;
  categories: string[];
  created_at: string;
  slug: string;
}

interface CategoryData {
  id: string;
  name: string;
  emoji?: string;
  slug?: string;
}

const ArticleGrid: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categoryData, setCategoryData] = useState<Record<string, CategoryData>>({});
  const currentLocale = useLocale();
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const supabase = createClient();
  const { theme } = useTheme();

  // Set up an IntersectionObserver to detect when the container is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect(); // Stop observing once visible
          }
        });
      },
      { threshold: 0.1 }
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // Only fetch articles when the container is visible
  useEffect(() => {
    if (!isVisible) return;

    async function fetchData() {
      try {
        // Step 1: Fetch latest 3 published articles
        const { data: articlesData, error: articlesError } = await supabase
          .from('articles')
          .select('id, title, slug, image, created_at')
          .eq('published', true)
          .order('created_at', { ascending: false })
          .limit(3);

        if (articlesError || !articlesData || articlesData.length === 0) {
          if (articlesError) {
            setArticles([]);
            return;
          }
          setArticles([]);
          return;
        }

        const articleIds = articlesData.map(article => article.id);

        // Step 2: Fetch categories for these articles using junction table
        const { data: junctions, error: junctionsError } = await supabase
          .from('article_categories_junction')
          .select('article_id, category_id')
          .in('article_id', articleIds);

        if (junctionsError || !junctions) {
          const articlesWithoutCategories = articlesData.map(article => ({
            ...article,
            categories: []
          }));
          setArticles(articlesWithoutCategories);
          return;
        }

        // Step 3: Create a map of article ID to category IDs
        const articleCategoriesMap: Record<string, string[]> = {};
        junctions.forEach((junction) => {
          if (!articleCategoriesMap[junction.article_id]) {
            articleCategoriesMap[junction.article_id] = [];
          }
          articleCategoriesMap[junction.article_id].push(junction.category_id);
        });

        // Step 4: Fetch all needed category data
        const allCategoryIds = Array.from(new Set(junctions.map(j => j.category_id)));
        
        if (allCategoryIds.length > 0) {
          // Fetch basic category data
          const { data: categoriesData, error: categoriesError } = await supabase
            .from('article_categories')
            .select('id, name, slug, emoji')
            .in('id', allCategoryIds);

          if (categoriesError) {
            console.error('Error fetching categories:', categoriesError);
          } else if (categoriesData) {
            // Create initial category data map
            const categoryMap: Record<string, CategoryData> = {};
            categoriesData.forEach(cat => {
              categoryMap[cat.id] = {
                id: cat.id,
                name: cat.name,
                emoji: cat.emoji,
                slug: cat.slug
              };
            });
            
            // For non-English locales, apply translations
            if (currentLocale !== 'en') {
              // Directly fetch and apply translations for the current locale
              const { data: translationsData } = await supabase
                .from('article_category_translations')
                .select('*')
                .in('category_id', allCategoryIds)
                .eq('locale', currentLocale);
                
              if (translationsData && translationsData.length > 0) {
                // Apply translations to the category data
                translationsData.forEach(translation => {
                  const categoryId = translation.category_id;
                  if (categoryMap[categoryId]) {
                    categoryMap[categoryId] = {
                      ...categoryMap[categoryId],
                      name: translation.name
                    };
                  }
                });
              }
            }
            
            setCategoryData(categoryMap);
            
            // Step 5: Combine articles with their categories
            const articlesWithCategories = articlesData.map(article => ({
              ...article,
              categories: articleCategoriesMap[article.id] || []
            }));

            // Step 6: If needed, fetch translations for article titles/slugs
            if (currentLocale !== 'en') {
              const { data: translationsData } = await supabase
                .from('article_translations')
                .select('article_id, title, slug')
                .in('article_id', articleIds)
                .eq('locale', currentLocale);

              if (translationsData && translationsData.length > 0) {
                // Create a map of article ID to translation data
                const translationMap: Record<string, any> = {};
                translationsData.forEach(trans => {
                  translationMap[trans.article_id] = trans;
                });

                // Apply translations to articles
                const translatedArticles = articlesWithCategories.map(article => {
                  const translation = translationMap[article.id];
                  if (translation) {
                    return {
                      ...article,
                      title: translation.title || article.title,
                      slug: translation.slug || article.slug
                    };
                  }
                  return article;
                });

                setArticles(translatedArticles);
              } else {
                setArticles(articlesWithCategories);
              }
            } else {
              setArticles(articlesWithCategories);
            }
          } else {
            // No categories found, just return articles without categories
            const articlesWithoutCategories = articlesData.map(article => ({
              ...article,
              categories: []
            }));
            setArticles(articlesWithoutCategories);
          }
        } else {
          // No categories found, just return articles without categories
          const articlesWithoutCategories = articlesData.map(article => ({
            ...article,
            categories: []
          }));
          setArticles(articlesWithoutCategories);
        }
      } catch {
        setArticles([]);
      }
    }

    fetchData();
  }, [isVisible, currentLocale, supabase]);

  return (
    <div
      className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
      ref={containerRef}
    >
      {articles.map((article) => (
        <div
          key={article.id}
          className="overflow-hidden transition rounded-xl bg-text-main"
        >
          <div className="relative h-48 w-full">
            <button
              onClick={() =>
                router.push(`/${currentLocale}${buildHref('/articles/[slug]', { slug: article.slug })}`)
              }
              className="w-full h-full"
            >
              {article.image ? (
                <div className="relative w-full h-full">
                  <Image
                    draggable={false} 
                    onContextMenu={(e) => e.preventDefault()} 
                    src={article.image}
                    alt={article.title}
                    fill
                    className="object-cover"
                  />
                  <div className="absolute top-4 right-4 w-12 h-12">
                    <Image
                      src={theme === 'dark' ? SpaceIconWhite : SpaceIconAbyss}
                      alt="Space Icon"
                      width={32}
                      height={32}
                      className="w-full h-full"
                    />
                  </div>
                </div>
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-primary-gradient-from to-primary-gradient-to" />
              )}
            </button>
          </div>
          <div className="py-4 px-4 bg-text-main">
            <div className="flex flex-wrap items-center gap-1">
              {article.categories.map((catId) => {
                const category = categoryData[catId];
                return (
                  <span 
                    key={catId} 
                    className="bg-primary/20 text-bg-main font-semibold text-sm px-2 py-1 rounded-xl"
                  >
                    {category?.emoji && <span className="mr-1">{category.emoji}</span>}
                    {category?.name || catId}
                  </span>
                );
              })}
              {article.categories.length > 0 && (
                <span className="text-bg-main mx-2 text-md">
                  {' • '}
                </span>
              )}
              <span className="text-accent/75 font-medium text-md">
                {new Date(article.created_at).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </span>
            </div>
            <h2 className="text-xl text-bg-main font-bold mb-4">
              <button
                onClick={() =>
                  router.push(`/${currentLocale}${buildHref('/articles/[slug]', { slug: article.slug })}`)
                }
                className="text-2xl hover:text-bg-secondary/90 text-left transition-colors"
              >
                {article.title}
              </button>
            </h2>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ArticleGrid;
